import { yupResolver } from '@hookform/resolvers/yup'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { Autocomplete, Button, Grid, InputAdornment } from '@mui/material'
import Modal from '@mui/material/Modal'
import { addHours, set } from 'date-fns'
import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { emitNotificationEvent } from 'src/events'
import {
  useGetCandidateInfoLazy,
  useGetCandidateResponsiblesInfoLazy,
  useGetIssuingAgencies,
  useGetSexTypes,
  useGetStates,
  useUpdateCandidateGeneralInfo,
  useGetRegisterTypesLazy,
} from 'src/services/backend'
import * as Yup from 'yup'
import { DatePicker } from '../DatePicker'
import { ErrorText } from '../ErrorText'
import { Loading } from '../Loading'
import { TextFieldCapitalize } from '../TextFieldCapitalize'
import { TextFieldComponent } from '../TextFieldComponent'
import { TextFieldMask } from '../TextFieldMask'
import TitleSectionAccordion from '../TitleSectionAccordion/TitleSectionAccordion'
import * as S from './styles'

interface IDefaultValue {
  candidateName: string
  candidateCpf: string
  sex: null | { pk_id: number; name: string }
  birth_date: string
  nationalityName: string
  cellphone: string
  emailCandidate: string
  motherName: string
  fatherName: string
  registerCode: string
  issue_entity: null | { pk_id: number; name: string; acronym: string }
  issue_state: null | { pk_id: number; name: string; acronym: string }
  issue_date: null | string
  cepCandidate: string
  streetCandidate: string
  residentialNumberCandidate: string
  complementCandidate: string
  neighborhoodCandidate: string
  cityCandidate: string
  stateCandidate: string
  responsibles: {
    responsible_personId: number
    responsible_name: string
    responsible_postalCode: string
    responsible_city: string
    responsible_email: string
    responsible_nationalityName: string
    responsible_neighborhood: string
    responsible_ufState: string
    responsible_cellphone: string
    responsible_publicPlace: string
    responsible_number: string
    responsible_complement: string
  }[]
}

const CandidateModal = ({
  registration,
  isOpen,
  onClose,
  getStateAcronyms,
}: any) => {
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [cepLoading, setCepLoading] = useState<boolean>(false)

  const currentStep = registration.getStep(registration, 'address')

  const getSexTypes = useGetSexTypes()
  const getStatesResult = useGetStates()
  const getIssuingAgenciesResult = useGetIssuingAgencies()
  const [getCandidateInfoLazy, getCandidateInfoLazyResult] =
    useGetCandidateInfoLazy()
  const [getCandidateResponsiblesInfo, getCandidateResponsiblesInfoResult] =
    useGetCandidateResponsiblesInfoLazy()
  const [updateCandidateGeneralInfo, updateCandidateGeneralInfoResp] =
    useUpdateCandidateGeneralInfo()
  const [getRegisterTypes, getRegisterTypesResult] = useGetRegisterTypesLazy()

  useEffect(() => {
    getRegisterTypes()
  }, [])

  const subscribedCourse = getRegisterTypesResult?.data?.response?.find(
    (course: any) =>
      course.pk_id === getCandidateInfoLazyResult?.data?.response?.courseTypeId
  )

  const currentDate = new Date()
  const dateValidationMessage = `O candidato deve ter entre ${
    currentDate.getFullYear() -
    addHours(new Date(subscribedCourse?.min_birthdate!), 3).getUTCFullYear()
  } e ${
    currentDate.getFullYear() -
    addHours(new Date(subscribedCourse?.max_birthdate!), 3).getUTCFullYear()
  } anos`

  const toasts = {
    errorGetStates: () =>
      emitNotificationEvent({
        id: 'errorGetStates',
        type: 'error',
        title: 'Ops!',
        message: 'Ocorreu um erro ao carregar as opções de "Estado"',
        isIndeterminate: false,
      }),
    errorGetSexTypes: () =>
      emitNotificationEvent({
        id: 'errorGetSexTypes',
        type: 'error',
        title: 'Ops!',
        message: 'Ocorreu um erro ao carregar as opções de "Sexo"',
        isIndeterminate: false,
      }),

    errorToLoadCep: () =>
      emitNotificationEvent({
        id: 'errorToLoadCep',
        type: 'error',
        title: 'Ops',
        message: 'Ocorreu um erro ao consultar o CEP',
        isIndeterminate: false,
      }),
    InvalidCep: () =>
      emitNotificationEvent({
        id: 'InvalidCep',
        type: 'error',
        title: 'Ops',
        message: 'Você precisa digitar um CEP válido!',
        isIndeterminate: false,
      }),
    cepNotFound: () =>
      emitNotificationEvent({
        id: 'cepNotFound',
        type: 'error',
        title: 'Ops',
        message: 'CEP não encontrado!',
        isIndeterminate: false,
      }),
    errorToSaveCandidateDocument: () =>
      emitNotificationEvent({
        id: 'errorToSaveCandidateDocument',
        type: 'error',
        message: 'Ocorreu um erro ao salvar as informações.',
      }),
    successPatchCandidate: () =>
      emitNotificationEvent({
        id: 'successPatchCandidate',
        type: 'success',
        message: 'As informações foram editadas com sucesso.',
      }),
  }

  const validationScheema = Yup.object().shape({
    candidateName: Yup.string().trim().required('Nome é obrigatório'),
    candidateCpf: Yup.string().required('CPF é obrigatório'),
    registerCode: Yup.string().required('RG é obrigatório'),
    sex: Yup.object()
      .shape({ pk_id: Yup.number(), name: Yup.string() })
      .nullable()
      .required('Sexo é obrigatório'),
    birth_date: (() => {
      let birthDateSchema = Yup.date()
        .nullable()
        .required('Data de nascimento é obrigatória')
        .test(
          'is-date-after-today',
          'Data de nascimento não pode ser maior que a data atual',
          (value) => value < currentDate
        )
        .typeError('Informe uma data de nascimento válida')

      if (subscribedCourse?.min_birthdate && subscribedCourse?.max_birthdate) {
        const maxBirthDateFormatted = set(
          addHours(new Date(subscribedCourse?.max_birthdate!), 3),
          { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }
        )

        const minBirthDateFormatted = set(
          addHours(new Date(subscribedCourse?.min_birthdate!), 3),
          { hours: 23, minutes: 59, seconds: 59, milliseconds: 999 }
        )

        birthDateSchema = birthDateSchema
          .max(minBirthDateFormatted, dateValidationMessage)
          .min(maxBirthDateFormatted, dateValidationMessage)
      }

      return birthDateSchema
    })(),
    nationalityName: Yup.string().required('Nacionalidade é obrigatória'),
    cellphone: Yup.string()
      .required('Whatsapp é obrigatório')
      .typeError('Informe um celular válido')
      .test(
        'have-length-sufficient',
        'O celular deve conter pelo menos 8 dígitos.',
        (value) => value.replaceAll('_', '')?.length > 10
      ),
    emailCandidate: Yup.string()
      .trim()
      .email('Informe um e-mail válido')
      .required('E-mail é obrigatório')
      .typeError('Informe um e-mail válido'),
    motherName: Yup.string().trim().required('Nome da mãe é obrigatório'),
    fatherName: Yup.string().trim(),
    issue_entity: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .required('Órgão expedidor é obrigatório'),
    issue_state: Yup.object()
      .shape({
        pk_id: Yup.number(),
        name: Yup.string(),
      })
      .nullable()
      .when('is_foreign', {
        is: false,
        then: (schema) => schema.required('Estado emissor é obrigatório'),
      }),
    issue_date: Yup.date()
      .nullable()
      .required('Data de emissão é obrigatória')
      .test(
        'issue-date',
        'Data de emissão deve ser maior ou igual a data de nascimento.',
        (value, { parent }) => {
          const { birth_date } = parent
          if (!birth_date || !value) {
            return true
          }

          const formattedBirthDate = new Date(birth_date).setHours(0, 0, 0, 0)
          const formattedIssueDate = new Date(value).setHours(0, 0, 0, 0)

          return formattedIssueDate >= formattedBirthDate
        }
      )
      .max(currentDate, 'Data de emissão não pode ser superior á data atual')
      .typeError('Informe uma data de emissão válida'),
    cepCandidate: Yup.string()
      .test(
        'have-length-sufficient',
        'CEP deve conter 8 dígitos',
        (value) => value?.replaceAll('-', '')?.length === 8
      )
      .required('Cep é obrigatório')
      .typeError('Informe um CEP válido'),
    streetCandidate: Yup.string()
      .required('Nome da rua é obrigatório')
      .typeError('Informe um nome de rua válido'),
    residentialNumberCandidate: Yup.number()
      .typeError('Informe um número válido')
      .positive('Números negativos ou 0 não são aceitos')
      .integer('Apenas números inteiros são aceitos'),
    complementCandidate: Yup.string().typeError(
      'Informe um complemento válido'
    ),
    neighborhoodCandidate: Yup.string()
      .required('Bairro é obrigatório')
      .typeError('Informe um bairro válido'),
    cityCandidate: Yup.string()
      .required('Cidade é obrigatória')
      .typeError('Informe uma cidade válida'),
    stateCandidate: Yup.string().required('Estado é obrigatório'),
    responsibles: Yup.array()
      .of(
        Yup.object().shape({
          responsible_name: Yup.string().required(
            'Nome completo é obrigatório'
          ),
          responsible_cellphone: Yup.string()
            .required('Celular é obrigatório')
            .typeError('Informe um celular válido'),
          responsible_email: Yup.string()
            .required('E-mail é obrigatório')
            .typeError('Informe um e-mail válido'),
          responsible_postalCode: Yup.string()
            .required('Cep é obrigatório')
            .typeError('Informe um CEP válido'),
          responsible_publicPlace: Yup.string()
            .required('Nome da rua é obrigatório')
            .typeError('Informe um nome de rua válido'),
          responsible_number: Yup.number()
            .required('Número da residência é obrigatório')
            .typeError('Informe um número válido'),
          responsible_neighborhood: Yup.string()
            .required('Bairro é obrigatório')
            .typeError('Informe um bairro válido'),
          responsible_city: Yup.string()
            .required('Cidade é obrigatória')
            .typeError('Informe uma cidade válida'),
          responsible_ufState: Yup.string().required('Estado é obrigatório'),
          responsible_nationalityName: Yup.string().required(
            'Nacionalidade é obrigatória'
          ),
        })
      )
      .required('Dados de responsável'),
  })

  type TValidationSchema = Yup.InferType<typeof validationScheema>

  const defaultValues: IDefaultValue = {
    candidateName: '',
    candidateCpf: '',
    sex: null,
    birth_date: '',
    cellphone: '',
    emailCandidate: '',
    motherName: '',
    fatherName: '',
    registerCode: '',
    issue_entity: null,
    issue_state: null,
    issue_date: null,
    cepCandidate: '',
    streetCandidate: '',
    residentialNumberCandidate: '',
    complementCandidate: '',
    neighborhoodCandidate: '',
    cityCandidate: '',
    stateCandidate: '',
    nationalityName: '',
    responsibles: [],
  }

  const {
    control,
    trigger,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(validationScheema),
    mode: 'all',
    reValidateMode: 'onChange',
  })

  const { fields } = useFieldArray({
    control: control as any,
    name: 'responsibles',
  })

  const getCep = (cep: string, candidate: boolean, i?: number) => {
    const translator = candidate
      ? {
          bairro: 'neighborhoodCandidate',
          logradouro: 'streetCandidate',
          localidade: 'cityCandidate',
          cep: 'cepCandidate',
          uf: 'stateCandidate',
        }
      : {
          bairro: `responsibles.${i}.responsible_neighborhood`,
          logradouro: `responsibles.${i}.responsible_publicPlace`,
          localidade: `responsibles.${i}.responsible_city`,
          cep: `responsibles.${i}.responsible_postalCode`,
          uf: `responsibles.${i}.responsible_ufState`,
        }

    const data = cep.replace('-', '').replace('_', '')
    const addressFormDataContext = currentStep?.formData
    if (data.length === 8) {
      if (cep === addressFormDataContext?.postal_code)
        handleSetFormData(addressFormDataContext, candidate)
      else {
        setCepLoading(true)
        fetch(`https://viacep.com.br/ws/${data}/json/`)
          .then((res) => res.json())
          .then((obj) => {
            if (obj.erro) {
              toasts.cepNotFound()
            }
            Object.keys(obj)
              .filter((prop) => prop in translator)
              .forEach((prop) => {
                //@ts-ignore
                setValue(translator[prop], obj[prop])
              })
            setCepLoading(false)
          })
          .catch((error) => {
            setCepLoading(false)
            toasts.errorToLoadCep()
          })
      }
    } else toasts.InvalidCep()
  }

  const handleSetFormData = (dataCandidate: any, dataResponsible: any) => {
    const formdata: TValidationSchema = {
      registerCode: dataCandidate.data.response.document.registerCode,
      issue_state: dataCandidate.data.response.document.issueState,
      issue_entity: dataCandidate.data.response.document.issueAgency,
      issue_date: addHours(
        new Date(dataCandidate.data.response.document.issueDate),
        3
      ),
      candidateName: dataCandidate.data.response.candidateInfo.name,
      candidateCpf: dataCandidate.data.response.candidateInfo.cpf,
      cellphone: dataCandidate.data.response.candidateInfo.cellphone,
      birth_date: addHours(
        new Date(dataCandidate.data.response.candidateInfo.birthDate),
        3
      ),
      nationalityName: dataCandidate.data.response.nationalityName,
      emailCandidate: dataCandidate.data.response.candidateInfo.email,
      sex: {
        pk_id: dataCandidate.data.response.candidateInfo.sexTypeId,
        name: dataCandidate.data.response.candidateInfo.sexTypeName,
      },
      motherName: dataCandidate.data.response.personsFiliation.motherName,
      fatherName: dataCandidate.data.response.personsFiliation.fatherName,
      cepCandidate: dataCandidate.data.response.address.postalCode,
      streetCandidate: dataCandidate.data.response.address.publicPlace,
      residentialNumberCandidate: dataCandidate.data.response.address.number,
      complementCandidate: dataCandidate.data.response.address.complement,
      neighborhoodCandidate: dataCandidate.data.response.address.neighborhood,
      cityCandidate: dataCandidate.data.response.address.city,
      stateCandidate: dataCandidate.data.response.address.ufState,

      responsibles: dataResponsible.data.response.map((res: any) => ({
        responsible_personId: res.responsibleInfo.personId,
        responsible_name: res.responsibleInfo.name,
        responsible_postalCode: res.address.postalCode,
        responsible_city: res.address.city,
        responsible_email: res.responsibleInfo.email,
        responsible_nationalityName: res.nationalityName,
        responsible_neighborhood: res.address.neighborhood,
        responsible_ufState: res.address.ufState,
        responsible_cellphone: res.responsibleInfo.cellphone,
        responsible_publicPlace: res.address.publicPlace,
        responsible_number: res.address.number,
        responsible_complement: res.address.complement,
      })),
    }

    Object.keys(formdata).forEach((field) => {
      //@ts-ignore
      setValue(field, formdata[field])
    })
  }

  const submitForm = (data: any) => {
    const candidateInfo = {
      personId: registration.candidate.candidateId,
      birthDate: data.birth_date,
      cellphone: data.cellphone,
      email: data.emailCandidate,
      address: {
        postalCode: data.cepCandidate.replaceAll('-', ''),
        publicPlace: data.streetCandidate,
        number: data.residentialNumberCandidate,
        complement: data.complementCandidate,
        neighborhood: data.neighborhoodCandidate,
        city: data.cityCandidate,
        ufState: data.stateCandidate,
      },
    }
    const responsibles = data.responsibles.map((responsible: any) => ({
      responsibleInfo: {
        personId: responsible.responsible_personId,
        email: responsible.responsible_email,
        cellphone: responsible.responsible_cellphone,
        address: {
          postalCode: responsible.responsible_postalCode.replaceAll('-', ''),
          publicPlace: responsible.responsible_publicPlace,
          number: responsible.responsible_number,
          complement: responsible.responsible_complement,
          neighborhood: responsible.responsible_neighborhood,
          city: responsible.responsible_city,
          ufState: responsible.responsible_ufState,
        },
      },
    }))

    updateCandidateGeneralInfo(registration.jwt, registration.code, {
      candidateInfo,
      responsibles,
    })
  }

  useEffect(() => {
    getCandidateResponsiblesInfo(registration.jwt, registration.code)
    getCandidateInfoLazy(registration.jwt, registration.code)
  }, [])

  useEffect(() => {
    if (
      getCandidateInfoLazyResult.data?.response &&
      getCandidateResponsiblesInfoResult.data?.response &&
      getIssuingAgenciesResult.data?.response &&
      getSexTypes.data?.response &&
      getStatesResult.data?.response
    ) {
      handleSetFormData(
        getCandidateInfoLazyResult,
        getCandidateResponsiblesInfoResult
      )
    }
  }, [
    getCandidateInfoLazyResult.data,
    getCandidateResponsiblesInfoResult.data,
    getSexTypes.data,
    getStatesResult.data,
    getIssuingAgenciesResult.data,
  ])

  useEffect(() => {
    if (updateCandidateGeneralInfoResp?.data) {
      toasts.successPatchCandidate()
      onClose()
    }
  }, [updateCandidateGeneralInfoResp?.data])

  useEffect(() => {
    if (getSexTypes?.error) toasts.errorGetSexTypes()
  }, [getSexTypes?.error])

  useEffect(() => {
    if (updateCandidateGeneralInfoResp.error)
      toasts.errorToSaveCandidateDocument()
  }, [updateCandidateGeneralInfoResp.error])

  return (
    <Modal open={isOpen} onClose={onClose}>
      <S.BoxContainer>
        <form onSubmit={handleSubmit(submitForm)}>
          <Grid container spacing={1}>
            <Grid item sm={12} xs={12}>
              <S.ModalTitle>
                <span>Código de inscrição: {registration.code}</span>
              </S.ModalTitle>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item sm={12} xs={12}>
              <S.ModalTitle>
                <span>Dados obrigatórios</span>
              </S.ModalTitle>
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="candidateCpf"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldMask
                    {...field}
                    mask="999.999.999-99"
                    label="CPF do(a) candidato(a)"
                    onChangeMask={(e) => {
                      field.onChange(e)
                    }}
                    error={!!fieldState.error}
                    value={field.value}
                    disabled
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item sm={12} xs={12}>
              <S.ModalTitle>
                <span>Dados do(a) candidato(a)</span>
              </S.ModalTitle>
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="candidateName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldCapitalize
                    {...field}
                    label={'Nome completo do(a) candidato(a)'}
                    error={!!fieldState.error}
                    value={field.value}
                    disabled
                  />
                )}
              />
              <ErrorText
                message={errors.candidateName && errors.candidateName.message}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="sex"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disabled
                    options={getSexTypes.data?.response || []}
                    getOptionLabel={(option) => {
                      if (typeof option === 'object' && option !== null) {
                        return option.name || ''
                      }
                      return option || ''
                    }}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        label="Sexo"
                        fullWidth
                        error={!!fieldState.error}
                      />
                    )}
                  />
                )}
              />
              <ErrorText message={errors.sex && errors.sex.message} />
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="birth_date"
                control={control}
                render={({ field, fieldState }) => (
                  <DatePicker
                    disabled={isDisabled}
                    {...field}
                    onChange={(value: Date | null) => {
                      field.onChange(value)
                      if (control._formValues.issue_date) trigger('issue_date')
                    }}
                    label="Data de nascimento"
                    inputFieldProps={{ error: !!fieldState.error }}
                    value={field.value}
                  />
                )}
              />
              <ErrorText
                message={errors.birth_date && errors.birth_date.message}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                name="cellphone"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldMask
                    disabled={isDisabled}
                    {...field}
                    mask="(99) 99999-9999"
                    value={field.value}
                    label="WhatsApp do(a) candidato(a)"
                    onChangeMask={(value) => {
                      field.onChange(value)
                    }}
                    error={!!fieldState.error}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <S.WhatsApp />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <ErrorText
                message={errors.cellphone && errors.cellphone.message}
              />
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="emailCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    value={field.value}
                    label="E-mail do(a) candidato(a)"
                    error={!!fieldState.error}
                    autoComplete="new-password"
                    disabled={isDisabled}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <S.Email />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              <ErrorText
                message={errors.emailCandidate && errors.emailCandidate.message}
              />
            </Grid>
            <Grid item xs={12} md={6} sm={6}>
              <Controller
                name="registerCode"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    label="RG do(a) candidato(a)"
                    error={!!fieldState.error}
                    value={field.value}
                    disabled
                  />
                )}
              />
            </Grid>
            <Grid container item xs={12} spacing={2}>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="issue_entity"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      disabled
                      options={getIssuingAgenciesResult?.data?.response || []}
                      getOptionLabel={(option) => {
                        if (typeof option === 'object' && option !== null) {
                          return option.acronym || ''
                        }
                        return option || ''
                      }}
                      onChange={(_, value) => field.onChange(value)}
                      renderInput={(params) => (
                        <TextFieldComponent
                          {...params}
                          label="Órgão expedidor"
                          fullWidth
                          error={!!fieldState.error}
                        />
                      )}
                    />
                  )}
                />

                <ErrorText
                  message={errors.issue_entity && errors.issue_entity.message}
                />
              </Grid>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="issue_state"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      disabled
                      options={getStatesResult?.data?.response || []}
                      getOptionLabel={(option) => {
                        if (typeof option === 'object' && option !== null) {
                          return option.acronym || ''
                        }
                        return option || ''
                      }}
                      onChange={(_, value) => field.onChange(value)}
                      renderInput={(params) => (
                        <TextFieldComponent
                          {...params}
                          label="Estado emissor"
                          fullWidth
                          error={!!fieldState.error}
                        />
                      )}
                    />
                  )}
                />
                <ErrorText
                  message={errors.issue_state && errors.issue_state.message}
                />
              </Grid>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="issue_date"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      sx={{
                        width: '100%',
                      }}
                      inputFieldProps={{ error: !!fieldState.error }}
                      label="Data de emissão"
                      value={field.value}
                      disabled
                    />
                  )}
                />
              </Grid>
              <Grid item lg={3} xs={12}>
                <Controller
                  name="nationalityName"
                  control={control}
                  render={({ field }) => (
                    <TextFieldComponent
                      {...field}
                      label="Nacionalidade"
                      value={field.value}
                      disabled
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container item xs={12} spacing={2}>
              <Grid item lg={6} xs={12}>
                <Controller
                  name="motherName"
                  control={control}
                  render={({ field }) => (
                    <TextFieldComponent
                      {...field}
                      label="Nome da mãe"
                      value={field.value}
                      disabled
                    />
                  )}
                />
              </Grid>
              <Grid item lg={6} xs={12}>
                <Controller
                  name="fatherName"
                  control={control}
                  render={({ field }) => (
                    <TextFieldComponent
                      {...field}
                      label="Nome do pai"
                      value={field.value}
                      disabled
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item sm={12} xs={12}>
              <S.ModalTitle>
                <span>Endereço do(a) candidato(a)</span>
              </S.ModalTitle>
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="cepCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldMask
                    disabled={isDisabled}
                    value={field.value}
                    onChangeMask={field.onChange}
                    onBlurMask={(event) => getCep(event.target.value, true)}
                    onKeyDownMask={(e) => {
                      if (e.keyCode === 13) {
                        e.preventDefault()
                        getCep(e.target.value, true)
                      }
                    }}
                    error={!!fieldState.error}
                    mask="99999-999"
                    label="CEP do(a) candidato(a)"
                    variant="outlined"
                  />
                )}
              />
              <ErrorText
                message={errors.cepCandidate && errors.cepCandidate.message}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="streetCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    disabled={isDisabled}
                    label="Nome da rua do(a) candidato(a)"
                    value={field.value}
                    error={!!fieldState.error}
                    onChange={field.onChange}
                  />
                )}
              />
              <ErrorText
                message={
                  errors.streetCandidate && errors.streetCandidate.message
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="residentialNumberCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    {...field}
                    disabled={isDisabled}
                    label="Número residencial do(a) candidato(a)"
                    error={!!fieldState.error}
                    onKeyDown={(e) => {
                      const re = /[0-9]/
                      if (!re.test(e.key) && e.key != 'Backspace') {
                        e.preventDefault()
                      }
                    }}
                  />
                )}
              />
              <ErrorText
                message={
                  errors.residentialNumberCandidate &&
                  errors.residentialNumberCandidate.message
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="neighborhoodCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    disabled={isDisabled}
                    label="Bairro do(a) candidato(a)"
                    value={field.value}
                    error={!!fieldState.error}
                    onChange={field.onChange}
                  />
                )}
              />
              <ErrorText
                message={
                  errors.neighborhoodCandidate &&
                  errors.neighborhoodCandidate.message
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="cityCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    disabled={isDisabled}
                    label="Cidade do(a) candidato(a)"
                    value={field.value}
                    error={!!fieldState.error}
                    onChange={field.onChange}
                  />
                )}
              />
              <ErrorText
                message={errors.cityCandidate && errors.cityCandidate.message}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="stateCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    disabled={isDisabled}
                    value={field.value}
                    options={getStateAcronyms()}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextFieldComponent
                        {...params}
                        error={!!fieldState.error}
                        label="Estado (UF) do(a) candidato(a)"
                      />
                    )}
                  />
                )}
              />
              <ErrorText
                message={errors.stateCandidate && errors.stateCandidate.message}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="complementCandidate"
                control={control}
                render={({ field, fieldState }) => (
                  <TextFieldComponent
                    disabled={isDisabled}
                    label="Complemento residencial do(a) candidato(a)"
                    value={field.value}
                    error={!!fieldState.error}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item sm={12} xs={12}>
              <S.ModalTitle>
                <span>
                  {getCandidateResponsiblesInfoResult.data?.response.length > 1
                    ? 'Dados dos(as) responsáveis'
                    : 'Dados do(a) responsável'}
                </span>
              </S.ModalTitle>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sm={6}
              style={{
                WebkitFlexBasis: '100%',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '2rem',
              }}
            >
              {fields.map((field: any, idx: number) => (
                <TitleSectionAccordion
                  title={`Responsável ${field.responsible_name}`}
                >
                  <S.ContainerResponsible>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sm={6}
                      style={{ maxWidth: '100%' }}
                    >
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_email` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldComponent
                            {...field}
                            value={field.value}
                            label="E-mail do(a) responsável"
                            error={!!fieldState.error}
                            autoComplete="new-password"
                            disabled={isDisabled}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <S.Email />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_email &&
                          errors.responsibles[idx]?.responsible_email?.message
                        }
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sm={6}
                      style={{ maxWidth: '100%' }}
                    >
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_nationalityName` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldComponent
                            {...field}
                            value={field.value}
                            label="Nacionalidade"
                            error={!!fieldState.error}
                            autoComplete="new-password"
                            disabled
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]
                            ?.responsible_nationalityName &&
                          errors.responsibles[idx]?.responsible_nationalityName
                            ?.message
                        }
                      />
                    </Grid>
                    <Grid item sm={6} xs={12} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_cellphone` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldMask
                            {...field}
                            value={field.value as string}
                            mask="(99) 99999-9999"
                            disabled={isDisabled}
                            label="WhatsApp do(a) responsável"
                            onChangeMask={(value) => {
                              field.onChange(value)
                            }}
                            error={!!fieldState.error}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <S.WhatsApp />
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_cellphone &&
                          errors.responsibles[idx]?.responsible_cellphone
                            ?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_postalCode` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldMask
                            disabled={isDisabled}
                            value={field.value as string}
                            onChangeMask={field.onChange}
                            onBlurMask={(event) =>
                              getCep(event.target.value, false, idx)
                            }
                            onKeyDownMask={(e) => {
                              if (e.keyCode === 13) {
                                e.preventDefault()
                                getCep(e.target.value, false, idx)
                              }
                            }}
                            error={!!fieldState.error}
                            mask="99999-999"
                            label="CEP do(a) responsável"
                            variant="outlined"
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_postalCode &&
                          errors.responsibles[idx]?.responsible_postalCode
                            ?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_publicPlace` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldComponent
                            disabled={isDisabled}
                            label="Nome da rua do(a) responsável"
                            value={field.value}
                            error={!!fieldState.error}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_publicPlace &&
                          errors.responsibles[idx]?.responsible_publicPlace
                            ?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_number` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldComponent
                            disabled={isDisabled}
                            type="number"
                            label="Número residencial do(a) responsável"
                            value={field.value}
                            error={!!fieldState.error}
                            onChange={field.onChange}
                            onKeyDown={(e) => {
                              const re = /[0-9]/
                              if (!re.test(e.key) && e.key != 'Backspace') {
                                e.preventDefault()
                              }
                            }}
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_number &&
                          errors.responsibles[idx]?.responsible_number?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_neighborhood` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldComponent
                            disabled={isDisabled}
                            label="Bairro do(a) responsável"
                            value={field.value}
                            error={!!fieldState.error}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_neighborhood &&
                          errors.responsibles[idx]?.responsible_neighborhood
                            ?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_city` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldComponent
                            disabled={isDisabled}
                            label="Cidade do(a) responsável"
                            value={field.value}
                            error={!!fieldState.error}
                            onChange={field.onChange}
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_city &&
                          errors.responsibles[idx]?.responsible_city?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_ufState` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <Autocomplete
                            {...field}
                            disabled={isDisabled}
                            value={field.value as string}
                            options={getStateAcronyms()}
                            onChange={(_, value) => field.onChange(value)}
                            renderInput={(params) => (
                              <TextFieldComponent
                                {...params}
                                error={!!fieldState.error}
                                label="Estado (UF) do(a) responsável"
                              />
                            )}
                          />
                        )}
                      />
                      <ErrorText
                        message={
                          errors.responsibles &&
                          errors.responsibles[idx]?.responsible_ufState &&
                          errors.responsibles[idx]?.responsible_ufState?.message
                        }
                      />
                    </Grid>
                    <Grid item xs={12} style={{ maxWidth: '100%' }}>
                      <Controller
                        name={
                          `responsibles.${idx}.responsible_complement` as keyof TValidationSchema
                        }
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextFieldComponent
                            disabled={isDisabled}
                            label="Complemento"
                            value={field.value}
                            error={!!fieldState.error}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </Grid>
                  </S.ContainerResponsible>
                </TitleSectionAccordion>
              ))}
            </Grid>
          </Grid>
          <S.ModalBoxButtons>
            <Button variant={'outlined'} onClick={onClose}>
              Cancelar
            </Button>
            {isDisabled ? (
              <Button
                variant={'contained'}
                endIcon={<EditIcon />}
                onClick={(e) => {
                  {
                    isDisabled ? setIsDisabled(false) : setIsDisabled(true)
                  }
                  e.preventDefault()
                }}
              >
                Editar
              </Button>
            ) : (
              <Button
                type="submit"
                variant={'contained'}
                endIcon={<SaveIcon />}
              >
                Salvar
              </Button>
            )}
          </S.ModalBoxButtons>
          <Loading isLoading={cepLoading} />
        </form>
      </S.BoxContainer>
    </Modal>
  )
}

export { CandidateModal }
